// =============================================================================
// AGC: [FLOW_TRACE] Frontend Flow Tracing Utility
// AGC: Generates flowRunId for lot creation flow and propagates through services
// =============================================================================

/**
 * AGC: [FLOW_TRACE] Generate a unique flow run ID
 * AGC: Format: flow-{timestamp}-{random}
 * AGC: This ID is used to correlate all DB operations for a single lot creation flow
 */
export const generateFlowRunId = (): string => {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 10)
  return `flow-${timestamp}-${random}`
}

/**
 * AGC: [FLOW_TRACE] Get or create flowRunId from existing data
 * AGC: Used when continuing an existing flow or creating a new one
 */
export const getOrCreateFlowRunId = (existingFlowRunId?: string): string => {
  if (existingFlowRunId && existingFlowRunId.startsWith('flow-')) {
    return existingFlowRunId
  }
  return generateFlowRunId()
}

/**
 * AGC: [FLOW_TRACE] Check if diagnostics endpoint is available
 * AGC: Only in development mode
 */
export const isDiagnosticsAvailable = (): boolean => {
  return import.meta.env.DEV === true || import.meta.env.MODE === 'development'
}

/**
 * AGC: [FLOW_TRACE] Fetch diagnostics for a specific lot
 * AGC: Returns null if not in dev mode or if request fails
 */
export const fetchLotDiagnostics = async (
  lotId: string,
  baseUrl: string,
  token?: string | null
): Promise<LotDiagnosticsReport | null> => {
  if (!isDiagnosticsAvailable()) {
    console.log('AGC: [FLOW_TRACE] Diagnostics not available in production')
    return null
  }

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${baseUrl}/api/v1/diagnostics/lot/${lotId}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      console.warn('AGC: [FLOW_TRACE] Diagnostics request failed:', response.status)
      return null
    }

    return await response.json()
  } catch (error) {
    console.warn('AGC: [FLOW_TRACE] Error fetching diagnostics:', error)
    return null
  }
}

/**
 * AGC: [FLOW_TRACE] Fetch full diagnostics for a specific lot
 * AGC: Includes all traces, events, tasks, plans, and inputs
 */
export const fetchFullLotDiagnostics = async (
  lotId: string,
  baseUrl: string,
  token?: string | null
): Promise<FullLotDiagnosticsReport | null> => {
  if (!isDiagnosticsAvailable()) {
    console.log('AGC: [FLOW_TRACE] Diagnostics not available in production')
    return null
  }

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${baseUrl}/api/v1/diagnostics/lot/${lotId}/full`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      console.warn('AGC: [FLOW_TRACE] Full diagnostics request failed:', response.status)
      return null
    }

    return await response.json()
  } catch (error) {
    console.warn('AGC: [FLOW_TRACE] Error fetching full diagnostics:', error)
    return null
  }
}

// =============================================================================
// AGC: [FLOW_TRACE] TYPE DEFINITIONS
// =============================================================================

export interface FlowTraceCounts {
  tasksCount: number
  inputsCount: number
  auditTraceCount: number
  auditEventCount: number
}

export interface LotDiagnosticsReport {
  lotId: string
  memoryReport: {
    found: boolean
    flowRunId?: string
    lotId?: string
    planId?: string | null
    fieldId?: string
    userId?: string
    startedAt?: string
    completedAt?: string | 'in_progress'
    durationMs?: number | null
    counts?: FlowTraceCounts
    totalDbWrites?: number
    successfulWrites?: number
    failedWrites?: number
    errors?: Array<{
      timestamp: string
      type: string
      path: string
      error: string
    }>
    message?: string
  }
  firestoreReport: {
    auditTraceCount: number
    auditEventCount: number
    tasksCount: number
    plansCount: number
    planIds: string[]
    traces: Array<{
      id: string
      timestamp: string
      version: string
      flowRunId: string | null
    }>
    events: Array<{
      id: string
      type: string
      timestamp: string
      flowRunId: string | null
    }>
    error?: string
  } | null
  timestamp: string
}

export interface FullLotDiagnosticsReport {
  lotId: string
  summary: {
    auditTraceCount: number
    auditEventCount: number
    tasksCount: number
    plansCount: number
    inputsCount: number
  }
  traces: Array<{
    id: string
    lotId: string
    timestamp: string
    version: string
    flowRunId?: string
    context?: Record<string, unknown>
    steps?: Array<Record<string, unknown>>
    results?: Record<string, unknown>
  }>
  events: Array<{
    id: string
    lotId: string
    type: string
    timestamp: string
    flowRunId?: string
    payload?: Record<string, unknown>
  }>
  tasks: Array<{
    id: string
    title: string
    category: string
    status: string
    flowRunId: string | null
  }>
  plans: Array<{
    id: string
    cropType: string
    createdAt: string
    flowRunId: string | null
  }>
  inputs: Array<{
    id: string
    planId: string
    name: string
    category: string
  }>
  memoryTrace: LotDiagnosticsReport['memoryReport']
  timestamp: string
}

// =============================================================================
// AGC: [FLOW_TRACE] DEV CONSOLE HELPERS
// =============================================================================

/**
 * AGC: [FLOW_TRACE] Log flow trace info to console (dev only)
 * AGC: Useful for debugging lot creation flow
 */
export const logFlowTrace = (flowRunId: string, stage: string, details?: Record<string, unknown>): void => {
  if (!isDiagnosticsAvailable()) return
  
  console.log(
    `🔍 AGC: [FLOW_TRACE:${flowRunId}] ${stage}`,
    details ? JSON.stringify(details, null, 2) : ''
  )
}

/**
 * AGC: [FLOW_TRACE] Print diagnostics summary to console
 */
export const printDiagnosticsSummary = (report: LotDiagnosticsReport | FullLotDiagnosticsReport): void => {
  if (!isDiagnosticsAvailable()) return
  
  console.group(`📊 AGC: [FLOW_TRACE] Diagnostics for lot: ${report.lotId}`)
  
  if ('summary' in report) {
    // Full report
    console.log('Summary:', report.summary)
    console.log('Traces:', report.traces?.length || 0)
    console.log('Events:', report.events?.length || 0)
    console.log('Tasks:', report.tasks?.length || 0)
    console.log('Plans:', report.plans?.length || 0)
    console.log('Inputs:', report.inputs?.length || 0)
  } else {
    // Basic report
    console.log('Memory Report:', report.memoryReport)
    console.log('Firestore Report:', report.firestoreReport)
  }
  
  console.groupEnd()
}



